import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Image as ImageIcon, X, Loader2, MessageCircle, Send, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, fmtDateTime } from '../lib/date';

const STATUS = ['Open', 'In Progress', 'Resolved', 'Wontfix'];
// Hoisted so useMemo deps stay stable — Open on top by design (iter 91).
const STATUS_PRIORITY = { 'Open': 0, 'In Progress': 1, 'Wontfix': 2, 'Resolved': 3 };
const REACTION_EMOJIS = ['👍', '✅', '🎉'];

// Structured Tab → Area menu — the areas surface only for the picked tab so
// admins triaging get a clean, groupable label.
const TAB_AREAS = {
  'Dashboard':          ['Referral pipeline flow', 'KPI tiles', 'Monthly BI tables', 'By-clinic charts', 'Other'],
  'Client Pipeline':    ['Search / filters', 'Add / edit client dialog', 'Added-on calendar', 'Column sorting', 'Other'],
  "Today's Actions":    ['New referrals', 'Paperwork chase', 'Ready for eval', 'Pre-auth', 'Waitlist / On hold', 'Other'],
  'Pending Notes':      ['Note status list', 'Compliance rollup', 'Other'],
  'Inquiries':          ['Google Sheet sync', 'Fax logging', 'Disposition', 'Other'],
  'Executive BI':       ['OD Bonus panel', 'Attendance metrics', 'Financial metrics', 'Monthly tables', 'Other'],
  'Clinician Performance': ['Report scope', 'Clinician cards', 'Show rate chart', 'Targets modal', 'Other'],
  'Clinician Roster':   ['Add / edit clinician', 'Termination date', 'Weekly target inline', 'Other'],
  'Data Import':        ['Attendance upload', 'Appt status upload', 'Claims / Income / Payer', 'Guardrail warning', 'Other'],
  'Weekly OD Digest':   ['Report frequency', 'Report contents', 'Delivery / email', 'Other'],
  'Ops Assistant chat': ['Chat responses', 'Client lookup', 'Session history', 'Other'],
  'Setup & Dropdowns':  ['Clinics', 'Referral sources', 'Insurance types', 'Other'],
  'Users & Roles':      ['Invite / add user', 'Role scoping', 'Sign-in', 'Other'],
  'Other':              ['General', 'Suggestion', 'Bug'],
};

const MAX_SCREENSHOT_BYTES = 1_500_000; // ~1.5 MB after base64 encoding

export default function Feedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('');
  const [area, setArea] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null); // { name, size, dataUrl }
  const [busy, setBusy] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [resolvingFor, setResolvingFor] = useState(null); // feedback doc being resolved
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolvingBusy, setResolvingBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null); // feedback_id whose thread is open
  const [replyText, setReplyText] = useState('');
  const [replyingBusy, setReplyingBusy] = useState(false);
  const [reactingId, setReactingId] = useState(null); // in-flight reaction target
  // ── Sort + status filter (iter 91) ────────────────────────────────────
  // Users asked for a sortable table with a status filter, defaulting to
  // "Open first". `sortKey` = which column drives the order, `sortDir` is
  // 1 (asc) / -1 (desc). Special key `default` = status priority (Open →
  // In Progress → Wontfix → Resolved) then newest-first — this is what
  // loads on first paint so open items always sit at the top.
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortKey, setSortKey] = useState('default');
  const [sortDir, setSortDir] = useState(-1);
  // Track which descriptions are "expanded" (user clicked to see all).
  // Kept off by default so long feedback stays compact in the table
  // — click the cell to toggle line-clamp on/off.
  const [expandedDesc, setExpandedDesc] = useState(() => new Set());
  const fileRef = useRef(null);

  const load = () => api.get('/feedback', { params: statusFilter === 'Archived' ? { include_archived: 1 } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [statusFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Derived: filtered + sorted list for the table body.
  const visibleItems = useMemo(() => {
    let list = (statusFilter === 'All' || statusFilter === 'Archived')
      ? [...items]
      : items.filter(f => (f.status || 'Open') === statusFilter);

    const cmp = (a, b) => {
      if (sortKey === 'default') {
        // Group by status priority (Open first), tie-break by newest.
        const pa = STATUS_PRIORITY[a.status ?? 'Open'] ?? 99;
        const pb = STATUS_PRIORITY[b.status ?? 'Open'] ?? 99;
        if (pa !== pb) return pa - pb;
        return (b.created_at || '').localeCompare(a.created_at || '');
      }
      const pick = (r) => {
        switch (sortKey) {
          case 'date':      return r.created_at || '';
          case 'submitter': return (r.submitter_name || '').toLowerCase();
          case 'tab':       return (r.tab || r.tab_area || '').toLowerCase();
          case 'area':      return (r.area || '').toLowerCase();
          case 'status':    return STATUS_PRIORITY[r.status ?? 'Open'] ?? 99;
          default:          return '';
        }
      };
      const va = pick(a), vb = pick(b);
      if (va < vb) return -1 * sortDir;
      if (va > vb) return  1 * sortDir;
      return 0;
    };
    list.sort(cmp);
    return list;
  }, [items, statusFilter, sortKey, sortDir]);

  // Per-status counts for the filter pills, computed once per items change.
  const statusCounts = useMemo(() => {
    const counts = { All: items.length, Archived: 0 };
    for (const s of STATUS) counts[s] = 0;
    for (const f of items) {
      if (f.archived_at) counts.Archived += 1;
      else counts[f.status || 'Open'] = (counts[f.status || 'Open'] || 0) + 1;
    }
    // When the user is currently viewing the Archived tab, `items`
    // holds archived rows only — surface that as the pill count.
    if (statusFilter === 'Archived') counts.Archived = items.length;
    return counts;
  }, [items, statusFilter]);

  // Toggle sort direction on repeat clicks; jumping to a new column resets
  // to descending (newest / highest first — the common intuition).
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(-1); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={11} className="ml-1 inline text-[#685B72]" />;
    return sortDir === 1
      ? <ArrowUp size={11} className="ml-1 inline text-[#685B72]" />
      : <ArrowDown size={11} className="ml-1 inline text-[#685B72]" />;
  };

  const HeaderCell = ({ col, label, className = '' }) => (
    <th
      onClick={() => toggleSort(col)}
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:text-[#2D2833] ${className}`}
      data-testid={`feedback-sort-${col}`}
    >
      {label}<SortIcon col={col} />
    </th>
  );

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file (PNG / JPG / WebP).');
      e.target.value = ''; return;
    }
    if (f.size > MAX_SCREENSHOT_BYTES) {
      toast.error(`Screenshot too large — keep under ${(MAX_SCREENSHOT_BYTES / 1_000_000).toFixed(1)} MB.`);
      e.target.value = ''; return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot({ name: f.name, size: f.size, dataUrl: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!description.trim()) return toast.error('Please describe the issue or suggestion.');
    if (!tab) return toast.error('Please pick which Tab this is about.');
    if (!area) return toast.error('Please pick the Area within that tab.');
    setBusy(true);
    try {
      await api.post('/feedback', {
        submitter_name: user.name,
        tab, area, description,
        screenshot_base64: screenshot?.dataUrl || null,
      });
      setTab(''); setArea(''); setDescription(''); setScreenshot(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Feedback submitted — thank you!');
      load();
    } catch (e) { toast.error('Submission failed'); }
    setBusy(false);
  };

  const updateStatus = async (id, status) => {
    // iter 80 — When flipping to Resolved, gather resolution notes so the
    // submitter can see how we fixed it (or what they should double-check).
    // Notes are optional; leaving blank still ships the notification.
    if (status === 'Resolved') {
      setResolvingFor(items.find(i => i.feedback_id === id));
      setResolutionNotes('');
      return;
    }
    await api.patch(`/feedback/${id}`, { status });
    load();
  };

  const submitResolution = async () => {
    if (!resolvingFor) return;
    setResolvingBusy(true);
    try {
      const res = await api.patch(`/feedback/${resolvingFor.feedback_id}`, {
        status: 'Resolved',
        resolution_notes: resolutionNotes.trim(),
      });
      if (res.data?.just_resolved) {
        toast.success('Marked resolved — submitter has been notified by email');
      } else {
        toast.success('Updated');
      }
      setResolvingFor(null);
      setResolutionNotes('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save resolution');
    } finally { setResolvingBusy(false); }
  };

  const areasForTab = tab && TAB_AREAS[tab] ? TAB_AREAS[tab] : [];

  const toggleThread = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
    setReplyText('');
  };

  const sendReply = async (feedbackId) => {
    const text = replyText.trim();
    if (!text) return toast.error('Reply cannot be empty.');
    setReplyingBusy(true);
    try {
      await api.post(`/feedback/${feedbackId}/reply`, { text });
      setReplyText('');
      await load();
      toast.success('Reply posted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post reply');
    } finally {
      setReplyingBusy(false);
    }
  };

  const toggleReaction = async (feedbackId, emoji) => {
    if (reactingId) return;
    setReactingId(feedbackId);
    try {
      const item = items.find(i => i.feedback_id === feedbackId);
      const mine = (item?.reactions || []).find(r => r.user_email === user?.email);
      // If clicking the same emoji I already have, remove it. Otherwise set it.
      const nextEmoji = (mine && mine.emoji === emoji) ? null : emoji;
      await api.post(`/feedback/${feedbackId}/reaction`, { emoji: nextEmoji });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update reaction');
    } finally {
      setReactingId(null);
    }
  };

  // Groups a feedback's reactions by emoji → count + who reacted (for tooltip).
  const reactionSummary = (f) => {
    const map = new Map();
    for (const r of (f.reactions || [])) {
      if (!map.has(r.emoji)) map.set(r.emoji, []);
      map.get(r.emoji).push(r.user_name || r.user_email);
    }
    return map;
  };

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#685B72]/70 font-semibold">Team feedback</div>
        <h1 className="font-serif text-4xl text-[#2D2833] mt-1">Feedback & Bug Log</h1>
        <p className="text-[#685B72] mt-1">Report issues, suggest improvements — the team reviews these weekly.</p>
      </div>

      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="feedback-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#685B72]">Tab</Label>
            <Select value={tab} onValueChange={(v) => { const nv = v === '__none__' ? '' : v; setTab(nv); setArea(''); }}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="feedback-tab"><SelectValue placeholder="Which page / tab?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="italic text-[#685B72]" data-testid="feedback-tab-none">— None —</SelectItem>
                {Object.keys(TAB_AREAS).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#685B72]">Area</Label>
            <Select value={area} onValueChange={(v) => setArea(v === '__none__' ? '' : v)} disabled={!tab}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="feedback-area"><SelectValue placeholder={tab ? 'Which area within that tab?' : 'Pick a tab first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="italic text-[#685B72]" data-testid="feedback-area-none">— None —</SelectItem>
                {areasForTab.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-[#685B72]">Describe the issue or suggestion</Label>
          <Textarea rows={4} value={description} onChange={e => setDescription(e.target.value)} className="rounded-xl mt-1" data-testid="feedback-desc" placeholder="What did you expect vs what happened? Steps to reproduce?" />
        </div>
        <div className="mt-4">
          <Label className="text-xs text-[#685B72]">Screenshot (optional)</Label>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#BAA9CA] hover:bg-[#F4F2F6] cursor-pointer text-sm" data-testid="feedback-screenshot-label">
              <ImageIcon size={14} /> {screenshot ? 'Replace image' : 'Attach screenshot'}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="feedback-screenshot-input" />
            </label>
            {screenshot && (
              <div className="flex items-center gap-2 text-[12px] text-[#685B72]" data-testid="feedback-screenshot-preview">
                <button
                  onClick={() => setPreviewSrc(screenshot.dataUrl)}
                  className="rounded-lg overflow-hidden border border-[#E8E5EC] hover:border-[#685B72]"
                  title="Click to preview"
                >
                  <img src={screenshot.dataUrl} alt="preview" className="h-10 w-10 object-cover" />
                </button>
                <span className="max-w-[220px] truncate">{screenshot.name}</span>
                <span className="text-[10px] text-[#685B72]">{(screenshot.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => { setScreenshot(null); if (fileRef.current) fileRef.current.value = ''; }} className="text-[#685B72]/60 hover:text-[#8A3A3A]" data-testid="feedback-screenshot-clear"><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="text-[10px] text-[#685B72] mt-1">PNG / JPG / WebP up to 1.5 MB. Stored inline with your report so the team can see exactly what you saw.</div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={busy} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="feedback-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Submitting…</> : 'Submit'}
          </Button>
        </div>
      </Card>

      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="feedback-list">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-serif text-xl text-[#2D2833]">Recent</h3>
          {/* Status filter pills (iter 91). "Open" first, default is All.
              iter 330: added "Archived" pill so super_user can see the
              auto-archived rows (Resolved/Wontfix >15 days old). */}
          <div className="inline-flex rounded-full border border-[#E8E5EC] overflow-hidden text-[11px]" data-testid="feedback-status-filter">
            {['All', ...STATUS, 'Archived'].map((s) => {
              const active = statusFilter === s;
              const count = statusCounts[s] ?? 0;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 font-semibold uppercase tracking-widest transition-colors ${active ? 'bg-[#685B72] text-white' : 'bg-white text-[#685B72] hover:bg-[#F1EDF5]'}`}
                  data-testid={`feedback-filter-${s.replace(/\W+/g, '_')}`}
                >
                  {s} <span className={`ml-1 ${active ? 'text-white/70' : 'text-[#685B72]'}`}>({count})</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr className="bg-[#FAF8FB] text-[#685B72] text-left">
                <HeaderCell col="date" label="Date" />
                <HeaderCell col="submitter" label="Submitter" />
                <HeaderCell col="tab" label="Tab" />
                <HeaderCell col="area" label="Area" />
                {/* Description gets a fixed cap so it stops eating the row.
                    Clicking a row toggles the clamp to "show all". */}
                <th className="px-3 py-2 font-semibold" style={{ width: 340, minWidth: 260, maxWidth: 340 }}>Description</th>
                <th className="px-3 py-2 font-semibold">Shot</th>
                <HeaderCell col="status" label="Status" />
                <th className="px-3 py-2 font-semibold">Thread</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-[#685B72]">
                  {items.length === 0
                    ? 'No feedback yet.'
                    : `No ${statusFilter.toLowerCase()} items right now.`}
                </td></tr>
              ) : visibleItems.map(f => (
                <React.Fragment key={f.feedback_id}>
                <tr className="border-t border-[#E8E5EC]">
                  <td className="px-3 py-2 text-[#685B72] whitespace-nowrap">{fmtDate(f.created_at)}</td>
                  <td className="px-3 py-2 text-[#2D2833]">{f.submitter_name}</td>
                  <td className="px-3 py-2 text-[#685B72]">{f.tab || f.tab_area || '—'}</td>
                  <td className="px-3 py-2 text-[#685B72]">{f.area || '—'}</td>
                  <td
                    className="px-3 py-2 text-[#685B72] align-top"
                    style={{ width: 340, minWidth: 260, maxWidth: 340 }}
                  >
                    <div
                      onClick={() => setExpandedDesc(prev => {
                        const next = new Set(prev);
                        next.has(f.feedback_id) ? next.delete(f.feedback_id) : next.add(f.feedback_id);
                        return next;
                      })}
                      className={`cursor-pointer whitespace-pre-wrap break-words leading-snug ${expandedDesc.has(f.feedback_id) ? '' : 'line-clamp-3'}`}
                      title={expandedDesc.has(f.feedback_id) ? 'Click to collapse' : 'Click to expand'}
                      data-testid={`feedback-desc-${f.feedback_id}`}
                    >
                      {f.description}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {f.screenshot_base64 ? (
                      <button onClick={() => setPreviewSrc(f.screenshot_base64)} className="rounded-md overflow-hidden border border-[#E8E5EC] hover:border-[#685B72]" data-testid={`feedback-shot-${f.feedback_id}`}>
                        <img src={f.screenshot_base64} alt="attachment" className="h-8 w-8 object-cover" />
                      </button>
                    ) : (<span className="text-[10px] text-[#685B72]">—</span>)}
                  </td>
                  <td className="px-3 py-2">
                    {['super_user', 'admin'].includes(user?.role) ? (
                      <Select value={f.status} onValueChange={v => updateStatus(f.feedback_id, v)}>
                        <SelectTrigger className="w-[130px] h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (<span className="text-xs">{f.status}</span>)}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleThread(f.feedback_id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-[#E8E5EC] hover:border-[#685B72] hover:bg-[#F4F2F6] text-[#685B72] whitespace-nowrap"
                      data-testid={`feedback-thread-toggle-${f.feedback_id}`}
                    >
                      <MessageCircle size={12} />
                      <span>{(f.replies?.length || 0)}</span>
                      {f.status === 'Resolved' && (f.reactions?.length > 0) && (
                        <span className="ml-1 text-[#B08A3A]" data-testid={`feedback-reaction-count-${f.feedback_id}`}>· {f.reactions.length}★</span>
                      )}
                    </button>
                  </td>
                </tr>
                {(f.resolution_notes || f.resolution) && (
                  <tr className="border-t border-[#E8E5EC] bg-[#FBF9F5]" data-testid={`feedback-resolution-row-${f.feedback_id}`}>
                    <td colSpan={8} className="px-3 py-2">
                      <div className="flex items-start gap-3 text-xs">
                        <span className="font-semibold text-[#B08A3A] uppercase tracking-widest text-[10px] mt-0.5">Resolution</span>
                        <div className="flex-1 text-[#685B72] whitespace-pre-wrap">{f.resolution_notes || f.resolution}</div>
                        {f.resolved_at && (
                          <span className="text-[#685B72] whitespace-nowrap">
                            {fmtDate(f.resolved_at)}{f.resolved_by ? ` · ${f.resolved_by}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {expandedId === f.feedback_id && (
                  <tr className="border-t border-[#E8E5EC] bg-[#F9F7FC]" data-testid={`feedback-thread-${f.feedback_id}`}>
                    <td colSpan={8} className="px-3 py-3">
                      {/* Reactions row (Resolved only) */}
                      {f.status === 'Resolved' && (() => {
                        const summary = reactionSummary(f);
                        const mine = (f.reactions || []).find(r => r.user_email === user?.email);
                        return (
                          <div className="flex items-center gap-2 mb-3" data-testid={`feedback-reactions-${f.feedback_id}`}>
                            <span className="text-[10px] uppercase tracking-widest font-semibold text-[#685B72]">React</span>
                            {REACTION_EMOJIS.map(em => {
                              const users = summary.get(em) || [];
                              const isMine = mine?.emoji === em;
                              return (
                                <button
                                  key={em}
                                  onClick={() => toggleReaction(f.feedback_id, em)}
                                  disabled={reactingId === f.feedback_id}
                                  title={users.length ? users.join(', ') : 'Be the first to react'}
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition ${
                                    isMine
                                      ? 'bg-[#EFE6FA] border-[#685B72] text-[#685B72] font-semibold'
                                      : 'bg-white border-[#E8E5EC] hover:border-[#685B72] text-[#685B72]'
                                  } ${reactingId === f.feedback_id ? 'opacity-60 cursor-wait' : ''}`}
                                  data-testid={`feedback-react-${em}-${f.feedback_id}`}
                                >
                                  <span className="text-sm leading-none">{em}</span>
                                  {users.length > 0 && <span>{users.length}</span>}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Reply chat thread */}
                      <div className="space-y-2 mb-3" data-testid={`feedback-replies-${f.feedback_id}`}>
                        {(f.replies || []).length === 0 ? (
                          <div className="text-xs text-[#685B72] italic">No replies yet — start the conversation.</div>
                        ) : (
                          f.replies.map(rp => {
                            const isMe = rp.author_email === user?.email;
                            return (
                              <div key={rp.reply_id || rp.at} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-xs ${
                                  isMe ? 'bg-[#EFE6FA] text-[#2D2833]' : 'bg-white border border-[#E8E5EC] text-[#2D2833]'
                                }`}>
                                  <div className="text-[10px] font-semibold text-[#685B72] mb-0.5">
                                    {rp.author_name}{!isMe ? '' : ' (you)'} · {fmtDateTime(rp.at)}
                                  </div>
                                  <div className="whitespace-pre-wrap leading-snug">{rp.text}</div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Reply composer */}
                      <div className="flex items-end gap-2">
                        <Textarea
                          rows={2}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a reply — the whole team can see it here."
                          className="flex-1 rounded-xl text-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              sendReply(f.feedback_id);
                            }
                          }}
                          data-testid={`feedback-reply-input-${f.feedback_id}`}
                        />
                        <Button
                          onClick={() => sendReply(f.feedback_id)}
                          disabled={replyingBusy || !replyText.trim()}
                          className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl h-9 px-3"
                          data-testid={`feedback-reply-send-${f.feedback_id}`}
                        >
                          {replyingBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        </Button>
                      </div>
                      <div className="text-[10px] text-[#685B72] mt-1">Tip: Ctrl / ⌘ + Enter to send</div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Screenshot preview overlay */}
      {previewSrc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreviewSrc(null)} data-testid="feedback-preview-overlay">
          <div className="bg-white rounded-xl p-2 max-w-[90vw] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={previewSrc} alt="screenshot" className="max-w-full max-h-[85vh]" />
            <div className="text-right mt-2"><button onClick={() => setPreviewSrc(null)} className="text-[#685B72] text-sm underline">Close</button></div>
          </div>
        </div>
      )}

      {/* Resolution notes modal (iter 80) */}
      {resolvingFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !resolvingBusy && setResolvingFor(null)} data-testid="feedback-resolve-modal">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#2D2833] mb-1">Resolve feedback</h3>
            <p className="text-xs text-[#685B72] mb-4">
              {resolvingFor.submitter_name} · {resolvingFor.tab_area || 'General'}
            </p>
            <div className="text-xs bg-[#FBF9F5] rounded-lg p-3 mb-4 text-[#685B72] max-h-32 overflow-y-auto whitespace-pre-wrap" data-testid="feedback-resolve-original">
              {resolvingFor.description || '(no description)'}
            </div>
            <Label className="text-xs font-semibold text-[#685B72]">Resolution notes <span className="font-normal text-[#685B72]">(optional — submitter will see these)</span></Label>
            <Textarea
              rows={5}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="e.g. Fixed the mismatched count on the Ready-for-Therapy tile — after your next redeploy it will match the pipeline stage total. Let me know if anything looks off."
              className="mt-1"
              data-testid="feedback-resolution-notes"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setResolvingFor(null)} disabled={resolvingBusy} data-testid="feedback-resolve-cancel">Cancel</Button>
              <Button onClick={submitResolution} disabled={resolvingBusy} className="bg-[#685B72] hover:bg-[#52475A] text-white" data-testid="feedback-resolve-confirm">
                {resolvingBusy ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : 'Resolve & notify submitter'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
